import { Box, Button, Card, CardContent, FormControl, InputLabel, MenuItem, useMediaQuery } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import theme from "theme";
import { P } from "theme/typography";
import {
  CaseFilterQueryParam,
  fetchClientLensCases,
  parseCaseFilterQueryParams,
  selectCasePropertySchemas,
  selectClientLens,
  selectClientLensAssignees,
  selectClientLensCases,
  selectClientLensCasesStatus,
  selectClientLensCasesTotal,
  setCaseFilters,
  TimeWhen,
} from "redux/slices/clientLens";
import { selectEnableFirmFeatureSet, selectMyUserId } from "redux/slices/session";
import CaseTable from "./CaseTable";
import RequestStatus from "redux/slices/RequestStatus";
import { AppRoutes } from "AppRoutes";
import PSelect from "components/PSelect";
import { CaseReviewStatus, GetLensCasesQueryVariables } from "generated/graphql";
import PersonalStats from "./PersonalStats";
import CaseStats from "./CaseStats";
import TextSelect from "components/TextSelect";
import {
  fetchCaseStats,
  selectReviewerCaseStats,
  selectReviewerTimeWhen,
  setReviewerTimeInterval,
} from "redux/slices/clientLens";
import CreateCase from "routes/lenses/lens/CreateCase";
import { Add } from "@mui/icons-material";
import LoadingPattern from "components/LoadingPattern";
import { selectActiveFirmIdN } from "redux/slices/client";
import CaseTableFilterByAssignee from "./CaseTableFilterByAssignee";
import { ALL_REVIEW_STATUSES, CaseClientStatus } from "Backend";
import { useNavigate } from "react-router-dom";
import useSearchParamsWithUpdate from "helpers/hooks/useSearchParamsWithUpdate";
import { useSnackbar } from "notistack";
import { setEquals } from "helpers/coll";

interface LensReviewerProps {
  lensId: number;
}

const EmptyLoading = (props) => {
  return (
    <Box
      sx={{
        gridArea: "content",
        width: "100%",
        height: "500px",
        display: "flex",
        justifyContent: "center",
      }}>
      <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "center" }}>{props.children}</Box>
    </Box>
  );
};

const showAllReviewStatusesValue = "";

const LensReviewer = (props: LensReviewerProps) => {
  const { lensId } = props;

  const isMobile = useMediaQuery(theme.breakpoints.only("xs"));
  const clientLens = useAppSelector(selectClientLens);
  const cases = useAppSelector(selectClientLensCases);
  const { searchParams, updateParam, updateParams } = useSearchParamsWithUpdate();
  const caseFilterParams = useMemo(() => parseCaseFilterQueryParams(searchParams), [searchParams]);
  const casesStatus = useAppSelector(selectClientLensCasesStatus);
  const casesTotal = useAppSelector(selectClientLensCasesTotal);
  const allAssignees = useAppSelector(selectClientLensAssignees);
  const caseStats = useAppSelector(selectReviewerCaseStats);
  const timeframe = useAppSelector(selectReviewerTimeWhen);
  const myUserId = useAppSelector(selectMyUserId);
  const dispatch = useAppDispatch();
  const [createCase, setCreateCase] = useState<boolean>(false);
  const assignees = useAppSelector(selectClientLensAssignees);
  const [hasInitiallyLoaded, setHasInitiallyLoaded] = useState<boolean>(false);
  const activeFirmId = useAppSelector(selectActiveFirmIdN);
  const showFirms = useAppSelector(selectEnableFirmFeatureSet);
  const caseProperties = useAppSelector(selectCasePropertySchemas);
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const navigateToCase = (caseId: number) => {
    navigate(AppRoutes.caseDetailBuilder(clientLens.id, caseId, ""));
  };

  const myAssignee = assignees.find((a) => a.id === myUserId);

  const caseStatusForBackend = caseFilterParams.caseStatus === "all" ? undefined : caseFilterParams.caseStatus;

  const fetchCases = () => {
    const filter: GetLensCasesQueryVariables = {
      lensId,
      textQuery: caseFilterParams.textQuery,
      caseStatus: caseStatusForBackend,
      reviewStatuses: caseFilterParams.reviewStatuses,
      plaintiffFirmId: caseFilterParams.plaintiffFirmId,
      offset: caseFilterParams.page * caseFilterParams.rowsPerPage,
      limit: caseFilterParams.rowsPerPage,
      assigneeId: myUserId,
      minPageCount: caseFilterParams.hasDocuments ? 1 : undefined,
      maxPageCount: caseFilterParams.hasDocuments === false ? 0 : undefined,
      unassigned: caseFilterParams.unassigned,
    };
    dispatch(setCaseFilters(searchParams.toString()));
    return dispatch(fetchClientLensCases(filter));
  };

  useEffect(() => {
    const fetchCaseStatsPromise = dispatch(fetchCaseStats({ lensId, firmId: activeFirmId, assigneeId: myUserId }));
    fetchCaseStatsPromise.unwrap().catch((error) => {
      if (error?.name !== "AbortError") {
        console.error(error);
        enqueueSnackbar("Failed to fetch case stats", { variant: "error" });
      }
    });
    return () => {
      fetchCaseStatsPromise.abort();
    };
  }, [lensId, activeFirmId, myUserId]);

  useEffect(() => {
    const thunk = fetchCases();
    setHasInitiallyLoaded(true);
    return () => thunk.abort();
  }, [lensId, caseFilterParams]);

  const updateWhen = (timeWhen: TimeWhen) => {
    dispatch(setReviewerTimeInterval({ timeWhen }));
  };

  const options = [
    {
      label: "This Week",
      value: TimeWhen.ThisWeek,
    },
    {
      label: "Last Week",
      value: TimeWhen.LastWeek,
    },
    {
      label: "This Month",
      value: TimeWhen.ThisMonth,
    },
    {
      label: "Last Month",
      value: TimeWhen.LastMonth,
    },
    {
      label: "This Year",
      value: TimeWhen.ThisYear,
    },
    {
      label: "Last Year",
      value: TimeWhen.LastYear,
    },
  ];

  const showEmptyState =
    casesStatus === RequestStatus.SUCCEEDED &&
    !cases.length &&
    caseFilterParams.textQuery === null &&
    !caseFilterParams.reviewStatuses &&
    !caseFilterParams.caseStatus;
  const showLoading = casesStatus === RequestStatus.PENDING && !hasInitiallyLoaded;

  const addFilter = (key: string, value: string | string[] | undefined) => {
    updateParams([
      { key, value },
      // always reset to page 1 when adding a filter in case the new filter reduces the number of pages
      { key: "page", value: "1" },
    ]);
  };

  return (
    <>
      {showEmptyState && (
        <EmptyLoading>
          <P variant="h4" mb={3}>
            No Cases Assigned
          </P>
          <Button color="primary" variant="contained" onClick={() => setCreateCase(true)}>
            Create A Case
          </Button>
        </EmptyLoading>
      )}
      {showLoading && (
        <EmptyLoading>
          <LoadingPattern />
        </EmptyLoading>
      )}
      {!showEmptyState && !showLoading && (
        <Box
          sx={{
            gridArea: "content",
            height: "100%",
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
          }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              justifyContent: { md: "space-around" },
              alignItems: { xs: "center", md: "flex-start" },
              mb: 2,
            }}>
            <Box sx={{ width: 300 }}>
              <Box mb={2} sx={{ textAlign: "center" }}>
                <P variant="h6">Your Cases ({caseStats.activeCount.toLocaleString()} active)</P>
              </Box>
              <Box sx={{ height: 150 }}>{myUserId !== undefined && <CaseStats center={["50%", "75%"]} />}</Box>
            </Box>
            <Box sx={{ maxWidth: 400 }}>
              <Box mb={2}>
                <P variant="h6">
                  <TextSelect
                    value={timeframe}
                    onChange={(event) => updateWhen(event.target.value)}
                    options={options}
                  />
                  in Pattern
                </P>
              </Box>
              <Card>
                <CardContent>
                  <PersonalStats lensId={lensId} />
                </CardContent>
              </Card>
            </Box>
          </Box>
          <Box
            sx={{
              minHeight: 0,
              display: "flex",
              height: "100%",
              flexDirection: "column",
            }}>
            <Box mb={2}>
              <P variant="h6">Cases</P>
            </Box>
            <Card
              sx={{
                height: "100%",
                minHeight: 300,
                display: "flex",
                flexDirection: "column",
              }}>
              <CardContent
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  height: "100%",
                  minHeight: 0,
                }}>
                <Box display="flex" sx={{ justifyContent: { xs: "center", sm: "flex-start" } }}>
                  {myUserId !== undefined && (
                    <CaseTableFilterByAssignee
                      label="Search by Claimant Name or Case ID"
                      sx={{ width: 300, mr: 1 }}
                      selection={caseFilterParams.textQuery}
                      setSelection={(s) => updateParam(CaseFilterQueryParam.SEARCH, s)}
                      lensId={lensId}
                      assigneeId={myUserId}
                    />
                  )}
                  {!isMobile && (
                    <>
                      <FormControl size="small" sx={{ width: 150, mr: 1 }}>
                        <InputLabel variant="outlined">Review Status</InputLabel>
                        <PSelect
                          multiple
                          label="Review Status"
                          value={
                            caseFilterParams.reviewStatuses || [CaseReviewStatus.New, CaseReviewStatus.NewDocuments]
                          }
                          onChange={(evt) => {
                            const selectedStatuses = evt.target.value as string[];
                            if (typeof selectedStatuses !== "object") return;
                            const paramName = CaseFilterQueryParam.REVIEW_STATUS;
                            const allSelected = setEquals(
                              new Set(selectedStatuses),
                              new Set(ALL_REVIEW_STATUSES.map((v) => v.value))
                            );
                            const noneSelected = selectedStatuses.length === 0;
                            if (noneSelected || allSelected || selectedStatuses.includes(showAllReviewStatusesValue)) {
                              addFilter(paramName, ["all"]);
                            } else {
                              addFilter(paramName, selectedStatuses);
                            }
                          }}>
                          <MenuItem value={showAllReviewStatusesValue}>Show All</MenuItem>
                          <MenuItem value="complete">Complete ({caseStats.completeCount})</MenuItem>
                          <MenuItem value="new">Pending ({caseStats.newCount})</MenuItem>
                          <MenuItem value="new_documents">
                            Pending - New Documents ({caseStats.newDocumentsCount})
                          </MenuItem>
                          <MenuItem value="no_documents">No Documents ({caseStats.noDocumentsCount})</MenuItem>
                        </PSelect>
                      </FormControl>

                      <FormControl size="small" sx={{ width: 150, mr: 1 }}>
                        <InputLabel variant="outlined">Case Status</InputLabel>
                        <PSelect
                          label="Case Status"
                          value={caseFilterParams.caseStatus || ""}
                          onChange={(evt) => {
                            const v = evt.target.value;
                            updateParam(CaseFilterQueryParam.CASE_STATUS, (v as CaseClientStatus) || undefined);
                          }}>
                          <MenuItem value="">Show All</MenuItem>
                          <MenuItem value="active">Active</MenuItem>
                          <MenuItem value="inactive">Inactive</MenuItem>
                        </PSelect>
                      </FormControl>

                      <Button
                        id="case_table_new"
                        color="default"
                        onClick={() => setCreateCase(true)}
                        startIcon={<Add />}>
                        New
                      </Button>
                    </>
                  )}
                </Box>

                <CaseTable
                  key={clientLens.id}
                  pagination={{
                    total: casesTotal,
                    page: caseFilterParams.page,
                    limit: caseFilterParams.rowsPerPage,
                  }}
                  caseProperties={caseProperties}
                  refreshCases={fetchCases}
                  loading={casesStatus === RequestStatus.PENDING}
                  cases={cases}
                  navigateToCase={navigateToCase}
                  clientLens={clientLens}
                  caseAssignees={allAssignees}
                  showFirms={showFirms}
                />
              </CardContent>
            </Card>
          </Box>
        </Box>
      )}
      {createCase && (
        <CreateCase onCancel={() => setCreateCase(false)} lens={clientLens} initialAssignee={myAssignee} />
      )}
    </>
  );
};

export default LensReviewer;
